import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {
    Icon,
    Typography,
    useAppDispatch,
    useAppSelector,
    FormInputText,
    FormTextEditor,
    Box,
} from '../../../base';
import {
    EmailTemplateStatus,
    emailTemplateSchema,
    LANGUAGES,
} from '../../../common';
import {
    emailTemplatesActions,
    emailTemplatesSelectors,
    organizationSelectors,
} from '../../../actions';
import {
    StyledDialog,
    StyledHeader,
    StyledTitle,
    StyledCloseButton,
    StyledSubjectRow,
    StyledSubjectLabel,
    StyledEditorWrapper,
    StyledPreviewFooter,
    StyledPargodaLogo,
    StyledActions,
    StyledDraftButton,
    StyledPublishButton,
    StyledNameField,
    StyledSubjectField,
} from './TemplateFormModal.styles';

interface ITemplateForm {
    name: string;
    subject: string;
    body: string;
}

const emptyForm: ITemplateForm = {
    name: '',
    subject: '',
    body: '',
};

const TemplateFormModal = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const open = useAppSelector(emailTemplatesSelectors.getEditorOpen);
    const targetTemplate = useAppSelector(
        emailTemplatesSelectors.getEditorTemplate
    );
    const isSaving = useAppSelector(
        emailTemplatesSelectors.getIsSavingTemplate
    );
    const companyInfo = useAppSelector(organizationSelectors.getCompanyInfo);

    const nameEditable = targetTemplate ? targetTemplate.name_editable : true;
    const isDraft = targetTemplate?.status === EmailTemplateStatus.DRAFT;
    const isPublished =
        targetTemplate?.status === EmailTemplateStatus.PUBLISHED;

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<ITemplateForm>({
        resolver: yupResolver(emailTemplateSchema(t, nameEditable)) as never,
        mode: 'onSubmit',
        defaultValues: emptyForm,
    });

    const onClose = () => dispatch(emailTemplatesActions.closeEditor());

    useEffect(() => {
        reset(
            targetTemplate
                ? {
                      name: targetTemplate.name,
                      subject: targetTemplate.subject,
                      body: targetTemplate.body,
                  }
                : emptyForm
        );
    }, [open, targetTemplate, reset]);

    const submit = (data: ITemplateForm, publish: boolean) => {
        if (targetTemplate) {
            dispatch(
                emailTemplatesActions.updateTemplate({
                    template: {
                        id: targetTemplate.id,
                        subject: data.subject,
                        body: data.body,
                        ...(nameEditable ? { name: data.name } : {}),
                    },
                    publish,
                })
            );
        } else {
            dispatch(
                emailTemplatesActions.createTemplate({
                    template: {
                        name: data.name,
                        language:
                            companyInfo?.defaultLanguage === LANGUAGES.HE
                                ? 2
                                : 1,
                        subject: data.subject,
                        body: data.body,
                    },
                    publish,
                })
            );
        }
        onClose();
    };

    const onSaveDraft = handleSubmit((data) => submit(data, false));
    const onPublish = handleSubmit((data) => submit(data, true));

    return (
        <StyledDialog open={open} onClose={onClose} maxWidth={false}>
            <Box>
                    <StyledHeader>
                        {nameEditable ? (
                            <StyledNameField>
                                <FormInputText
                                    name="name"
                                    control={control}
                                    size="small"
                                    removeBottomPadding
                                    placeholder={t(
                                        'settings.emailTemplates.form.namePlaceholder'
                                    )}
                                />
                            </StyledNameField>
                        ) : (
                            <StyledTitle variant="h6">
                                {targetTemplate?.name}
                            </StyledTitle>
                        )}
                        <StyledCloseButton onClick={onClose} size="small">
                            <Icon name="CloseIcon" size={22} />
                        </StyledCloseButton>
                    </StyledHeader>

                    <StyledSubjectRow>
                        <StyledSubjectLabel error={Boolean(errors.subject)}>
                            {t('settings.emailTemplates.form.subjectLabel')}
                        </StyledSubjectLabel>
                        <StyledSubjectField>
                            <FormInputText
                                name="subject"
                                control={control}
                                size="small"
                                removeBottomPadding
                                placeholder={t(
                                    'settings.emailTemplates.form.subjectPlaceholder'
                                )}
                            />
                        </StyledSubjectField>
                    </StyledSubjectRow>

                    <StyledEditorWrapper>
                        <FormTextEditor name="body" control={control} />
                    </StyledEditorWrapper>

                    <StyledPreviewFooter
                        container
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                    >
                        <StyledPargodaLogo
                            src="/assets/logoIcon.svg"
                            alt="pargoda"
                        />
                        <Typography variant="caption">pargoda.com</Typography>
                    </StyledPreviewFooter>

                    <StyledActions>
                        {!nameEditable ? (
                            <StyledPublishButton
                                variant="contained"
                                onClick={onPublish}
                                disabled={isSaving}
                            >
                                {t('settings.emailTemplates.form.save')}
                            </StyledPublishButton>
                        ) : (
                            <>
                                {!isPublished && (
                                    <StyledDraftButton
                                        variant="outlined"
                                        onClick={onSaveDraft}
                                        disabled={isSaving}
                                    >
                                        {isDraft
                                            ? t(
                                                  'settings.emailTemplates.form.save'
                                              )
                                            : t(
                                                  'settings.emailTemplates.form.saveDraft'
                                              )}
                                    </StyledDraftButton>
                                )}
                                <StyledPublishButton
                                    variant="contained"
                                    onClick={onPublish}
                                    disabled={isSaving}
                                >
                                    {isDraft
                                        ? t(
                                              'settings.emailTemplates.form.saveAndPublish'
                                          )
                                        : t(
                                              'settings.emailTemplates.form.publish'
                                          )}
                                </StyledPublishButton>
                            </>
                        )}
                    </StyledActions>
                </Box>
        </StyledDialog>
    );
};

export default TemplateFormModal;
